import { QueryHookOptions } from '@apollo/client';
import { UsageLimitEntitlementName } from '@jupiterone/entitlement-types';
import gql from 'graphql-tag';
import { createUseQueryHook } from '../apollo/createUseQueryHook';
import { useEffect } from 'react';
import { useSnackbarNotification } from '../components/notifications/SnackbarNotificationContext';

export const CheckUsageLimitEntitlementAccess = gql`
  query checkUsageLimitEntitlementAccess($usageLimitEntitlementName: String!) {
    checkUsageLimitEntitlementAccess(
      usageLimitEntitlementName: $usageLimitEntitlementName
    ) {
      canAccess
      usage
      limit
    }
  }
`;

type Variables = {
  usageLimitEntitlementName: UsageLimitEntitlementName;
};
type Response = {
  checkUsageLimitEntitlementAccess: {
    canAccess: boolean;
    usage: number;
    limit: number;
  };
};

export const useUsageLimitEntitlementAccessQuery = (
  options: QueryHookOptions<Response, Variables>,
  suppressError?: boolean
) => {
  const notification = useSnackbarNotification();
  const useQuery = createUseQueryHook<Response, Variables>(
    CheckUsageLimitEntitlementAccess
  );

  const { data, ...rest } = useQuery(options);

  useEffect(() => {
    if (rest.error && !suppressError) {
      notification.error({
        error: rest.error,
        message:
          "We couldn't fetch your usage data. Please refresh the page to try again.",
      });
    }
  }, [rest.error, suppressError]);

  const { canAccess = false, usage = 0, limit = 0 } =
    data?.checkUsageLimitEntitlementAccess || {};

  const hasUnlimitedUsage = canAccess && limit === -1;

  return {
    canAccess,
    usage,
    limit,
    hasUnlimitedUsage,
    ...rest,
  };
};
